'use client'

import { useEffect, useRef, useState } from 'react'

export interface BazaarvoiceRatingObservation {
  readonly containerRef: React.RefObject<HTMLDivElement | null>
  /** `null` mientras Bazaarvoice no ha pintado nada reconocible dentro del
   * contenedor observado. */
  readonly reviewCount: number | null
  readonly ratingAverage: number | null
}

/**
 * El widget `inline_rating` de Bazaarvoice renderiza su recuento/media como
 * SVG (estrellas con gradiente) — no como texto plano — pero SIEMPRE añade un
 * nodo `.bv-off-screen` (accesible, visualmente oculto) con una frase fija en
 * español: `"{media} de 5 estrellas. {N} reseña(s)"`. Confirmado inspeccionando
 * el DOM real de un widget cargado contra Bazaarvoice (staging) con
 * `agent-browser` (specs/046-valoraciones-bazaarvoice-bd) — no depende de
 * clases internas del widget (más propensas a cambiar entre versiones) ni de
 * parsear el relleno de las estrellas SVG.
 *
 * **No usar microdatos `itemprop`** (primer intento, descartado): el propio
 * contenedor se renderiza con `data-bv-seo="false"` (fiel al markup de
 * referencia de eso-entradas), que desactiva a propósito el marcado SEO de
 * Bazaarvoice — `itemprop="ratingValue"/"reviewCount"` nunca aparece con ese
 * atributo activo, por diseño, no por un fallo de carga.
 *
 * El propio widget mezcla idiomas: confirmado con `agent-browser` contra un
 * producto real en staging, el recuento a veces llega en inglés
 * (`"5.0 de 5 estrellas. 1 review para ..."`) en vez de `"1 reseña"` —
 * probablemente una clave de traducción de Bazaarvoice sin completar para el
 * singular. Sin la alternativa `reviews?`, el regex no matchea nunca y el
 * rating se oculta en silencio (rating "desaparecido" en hero y cards).
 */
const OFF_SCREEN_SELECTOR = '.bv-off-screen'
const RATING_TEXT_PATTERN = /([\d]+(?:[.,]\d+)?)\s+de\s+5\s+estrellas\.\s+(\d+)\s+(?:rese[ñn]as?|reviews?)/i

function parsePositive(value: string): number | null {
  const numeric = Number(value.replace(',', '.'))
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null
}

function readAggregateRating(container: HTMLElement): { reviewCount: number | null; ratingAverage: number | null } {
  const text = container.querySelector(OFF_SCREEN_SELECTOR)?.textContent ?? ''
  const match = RATING_TEXT_PATTERN.exec(text)
  if (!match) return { reviewCount: null, ratingAverage: null }
  const [, averageRaw, countRaw] = match
  return { ratingAverage: parsePositive(averageRaw!), reviewCount: parsePositive(countRaw!) }
}

/**
 * Observa el contenedor de Bazaarvoice (`inline_rating` en cards/cabecera —
 * ver R12 en research.md, `reviews` no expone el agregado, solo reseñas
 * individuales) con `MutationObserver` y extrae el conteo/media reales, para
 * poder aplicarles el mismo criterio de umbral mínimo que el sistema propio
 * (spec.md FR-009, research.md R3). Mientras no se detecta nada reconocible,
 * ambos valores quedan en `null` — fallback seguro: el contenedor permanece
 * oculto, nunca se inventa un dato.
 */
export function useBazaarvoiceRatingObserver(): BazaarvoiceRatingObservation {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [reviewCount, setReviewCount] = useState<number | null>(null)
  const [ratingAverage, setRatingAverage] = useState<number | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    function readFromDom() {
      if (!container) return
      const { reviewCount: nextReviewCount, ratingAverage: nextRatingAverage } = readAggregateRating(container)
      setReviewCount((current) => (current === nextReviewCount ? current : nextReviewCount))
      setRatingAverage((current) => (current === nextRatingAverage ? current : nextRatingAverage))

      // Bazaarvoice pinta su marcado de golpe y no lo vuelve a tocar una vez
      // resuelto — con ambos valores leídos, desconectar evita mantener un
      // `MutationObserver` vivo el resto de la vida del componente: coste que
      // se multiplica por cada card de un listado de 20-50 elementos (cada
      // una con su propia instancia de este hook).
      if (nextReviewCount !== null && nextRatingAverage !== null) {
        observer.disconnect()
      }
    }

    // `observer` se crea antes de la primera lectura (en vez de tras ella)
    // precisamente para que `readFromDom` pueda desconectarlo desde dentro de
    // sí misma, incluida esa primera llamada síncrona.
    const observer = new MutationObserver(readFromDom)

    readFromDom()

    observer.observe(container, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  return { containerRef, reviewCount, ratingAverage }
}
